import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { App, Button, Card, Select, Space, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { mutations, queries } from '../../../graphql/graphql';
import MapList, { MapListNextPayload } from '../../Map/MapList';
import { PlanView } from '../../Plans/View';

const { Title, Paragraph, Text } = Typography;

type Step = 'map' | 'plan' | 'contractor';

export const AdminProposalCreate: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('map');
  const [selection, setSelection] = useState<MapListNextPayload | null>(null);
  const [contractorId, setContractorId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { data: contractorsData, loading: contractorsLoading } = useQuery(
    queries.contractors.CONTRACTORS,
    {
      variables: { search: search || undefined },
      fetchPolicy: 'network-only',
    }
  );

  const contractorOptions = useMemo(() => {
    const items = contractorsData?.contractors || [];
    return items.map((contractor: { id: string; name?: string | null; email?: string | null }) => ({
      label: contractor.name || contractor.email || contractor.id,
      value: contractor.id,
    }));
  }, [contractorsData?.contractors]);

  const [proposalCreate, { loading: creating }] = useMutation(mutations.proposal.PROPOSAL_CREATE);

  const handleMapNext = useCallback(
    async (data?: MapListNextPayload) => {
      if (!data?.planId || !data.mapResultId) {
        message.error('Please select a plan and complete the map step.');
        return;
      }
      setSelection(data);
      setStep('plan');
    },
    [message]
  );

  const handlePlanNext = useCallback(() => {
    if (!selection?.planId || !selection.mapResultId) {
      message.error('Plan or map selection is missing.');
      return;
    }
    setStep('contractor');
  }, [message, selection?.mapResultId, selection?.planId]);

  const handleCreate = useCallback(async () => {
    if (creating) {
      return;
    }
    if (!contractorId) {
      message.error('Please select a contractor.');
      return;
    }
    if (!selection?.planId || !selection.mapResultId) {
      message.error('Plan or map selection is missing.');
      return;
    }

    try {
      const res = await proposalCreate({
        variables: {
          input: {
            contractorId,
            planId: selection.planId,
            mapResultId: selection.mapResultId,
          },
        },
      });
      const proposalId = res.data?.proposalCreate?.id;
      if (!proposalId) {
        message.error('Failed to create proposal.');
        return;
      }
      message.success('Proposal created.');
      navigate(`/admin/proposal/edit/${proposalId}`);
    } catch (error: any) {
      message.error(error?.message || 'Failed to create proposal.');
    }
  }, [contractorId, creating, message, navigate, proposalCreate, selection]);

  return (
    <div>
      {step === 'plan' ? (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Button onClick={() => setStep('map')}>Back to map</Button>
          </Space>
          <PlanView id={selection?.planId} mapId={selection?.mapResultId} onNext={handlePlanNext} />
        </>
      ) : step === 'contractor' ? (
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Title level={4} style={{ marginBottom: 4 }}>
                Select contractor
              </Title>
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                Choose the contractor who will handle this proposal.
              </Paragraph>
            </div>
            <Select
              showSearch
              placeholder="Search contractor"
              value={contractorId || undefined}
              loading={contractorsLoading}
              onSearch={value => setSearch(value)}
              onChange={value => setContractorId(value)}
              filterOption={false}
              allowClear
              options={contractorOptions}
              style={{ width: '100%' }}
            />
            <Space>
              <Button onClick={() => setStep('plan')}>Back to plan</Button>
              <Button type="primary" loading={creating} onClick={handleCreate}>
                Create proposal
              </Button>
            </Space>
          </Space>
        </Card>
      ) : (
        <MapList queries={queries.plans.PLANS} onNext={handleMapNext} />
      )}
    </div>
  );
};

export default AdminProposalCreate;
